/**
 * Client for the MDR console (backend: api/empi_explorer/).
 * TENANT-SCOPED, READ ONLY.
 *
 * ACCESS IS TWO-TIER. Contracts and feeds need module_data_mdr; anything
 * returning a name, DOB or identifier also needs data_phi. A user with MDR but
 * not PHI gets 403 on the patient endpoints — the UI hides those tabs rather
 * than letting them fail.
 *
 * Reads the PUBLISHED EMPI run from KAIRON_EDW.EMPI. There is no run
 * parameter: publication is an atomic schema swap, so exactly one run exists.
 *
 * Everything here is at PERSON grain (PATIENT_MASTER), never BENE_MBI_ID —
 * eliminating that join key is the reason EMPI exists.
 *
 * Uses the global axios instance (baseURL, tenant_slug interceptor, 401 handler
 * from main.js). Paths relative.
 */
import axios from 'axios';

const BASE = '/api/empi_explorer';

/**
 * The grid: one row per contract, one column per performance year.
 * Returns { years, cells } — flat cells, pivoted client-side.
 */
export async function getContractGrid() {
  const { data } = await axios.get(`${BASE}/contracts/`);
  return data;
}

/** The roster for one contract-year. */
export async function listContractPatients(cohortMaster, performanceYear, limit = 500) {
  const { data } = await axios.get(
    `${BASE}/contracts/${encodeURIComponent(cohortMaster)}/patients/`,
    { params: { performance_year: performanceYear, limit } },
  );
  return data;
}

/** The deliveries that resolved into one contract — the grid's audit trail. */
export async function getContractLineage(contractId) {
  const { data } = await axios.get(
    `${BASE}/contracts/${encodeURIComponent(contractId)}/lineage/`,
  );
  return data;
}

/**
 * Every asserted feature for one patient — identity, demographics, contact and
 * assignment — with its corroboration. Winners and losers together.
 */
export async function getPatientEvidence(patientMaster) {
  const { data } = await axios.get(
    `${BASE}/patients/${encodeURIComponent(patientMaster)}/evidence/`,
  );
  return data;
}

/**
 * ONE search box -> people. Accepts an MBI, member id, MRN, composite EHR id
 * ('CLIENT|CHART') or a name ('Smith, Jane'). The backend classifies the term.
 *
 * Identifier terms match EXACTLY; names match as a prefix. Hits carry
 * MATCHED_ID_TYPE/VALUE so a match on a superseded MBI is visible.
 */
export async function searchPatients(term, limit = 50) {
  const { data } = await axios.get(`${BASE}/patients/search/`, {
    params: { q: term, limit },
  });
  return data;
}

/**
 * An identifier -> its PATIENT_MASTER(s).
 *
 * Deep links carry an IDENTIFIER, never a master id: PATIENT_MASTER is a uuid5
 * recomputed every weekly run, so a bookmarked one stops resolving. More than
 * one master back means two people share the value — a real finding.
 */
export async function resolvePatientIdentifier(idValue) {
  const { data } = await axios.get(`${BASE}/patients/resolve/`, {
    params: { id: idValue },
  });
  return data;
}

/** Header for one patient: identity, counts, and the contracts they are in. */
export async function getPatientSummary(patientMaster) {
  const { data } = await axios.get(
    `${BASE}/patients/${encodeURIComponent(patientMaster)}/`,
  );
  return data;
}

/**
 * Per-source contribution to IDENTITY — records, people, assertions,
 * reliability rank and last delivery. Aggregates only, so no PHI grant needed.
 */
export async function getFeedCoverage() {
  const { data } = await axios.get(`${BASE}/feeds/`);
  return data;
}
